import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { BookmarkListItem } from 'markcat-domain';
import { apiFetch } from '../client';
import { BULK_IDS_MAX, chunkIds } from '../limits';
import { patchRows, removeRows } from '../queries/bookmarkEvents';
import { invalidateBookmarkData } from '../queries/useBookmarks';

// Every bulk mutationFn chunks its ids to ≤BULK_IDS_MAX per request — the server
// caps id lists at 1000 (service/models.py). Chunks go out sequentially; a failed
// chunk rejects the whole mutation. Optimistic patches land in onMutate the way
// events do, and onSettled's invalidate reads every window back — after a
// failure too, with whatever the completed chunks already changed server-side.

// Keys that reflect bookmark state and should refresh after any bulk op that
// changes the bookmark set (including the exact bookmark-summary sidebar
// counts). `refetchType: 'active'` restricts the refetch to queries
// with a live observer — inactive queries refresh on next mount via their own
// staleTime policy.
function invalidateBookmarkDerived(qc: QueryClient) {
  invalidateBookmarkData(qc);
  qc.invalidateQueries({ queryKey: ['progress'], refetchType: 'active' });
}

export function useBulkDelete() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (ids: string[]) => {
      for (const chunk of chunkIds(ids, BULK_IDS_MAX)) {
        await apiFetch('/api/bookmarks/delete-batch', {
          method: 'POST',
          body: JSON.stringify({ ids: chunk }),
        });
      }
    },
    // Only the rows a window holds move its count: ids beyond the loaded window
    // would otherwise over-decrement it until the next read.
    onMutate: (ids) => removeRows(queryClient, new Set(ids)),
    onSettled: () => invalidateBookmarkDerived(queryClient),
  });
}

export function useBulkRestore() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (ids: string[]) => {
      for (const chunk of chunkIds(ids, BULK_IDS_MAX)) {
        await apiFetch('/api/bookmarks/restore-batch', {
          method: 'POST',
          body: JSON.stringify({ ids: chunk }),
        });
      }
    },
    onSettled: () => invalidateBookmarkDerived(queryClient),
  });
}

export function useBulkReExtract() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (ids: string[]) => {
      for (const chunk of chunkIds(ids, BULK_IDS_MAX)) {
        await apiFetch('/api/bookmarks/re-extract', {
          method: 'POST',
          body: JSON.stringify({ ids: chunk }),
        });
      }
    },
    onSettled: () => invalidateBookmarkDerived(queryClient),
  });
}

export function useBulkReClassify() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (ids: string[]) => {
      for (const chunk of chunkIds(ids, BULK_IDS_MAX)) {
        await apiFetch('/api/bookmarks/re-classify', {
          method: 'POST',
          body: JSON.stringify({ ids: chunk }),
        });
      }
    },
    onSettled: () => invalidateBookmarkDerived(queryClient),
  });
}

export function useBulkRefineSubcategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (ids: string[]) => {
      for (const chunk of chunkIds(ids, BULK_IDS_MAX)) {
        await apiFetch('/api/bookmarks/refine-subcategory', {
          method: 'POST',
          body: JSON.stringify({ ids: chunk }),
        });
      }
    },
    onSettled: () => invalidateBookmarkDerived(queryClient),
  });
}

export function useBulkUpdate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ ids, updates }: { ids: string[]; updates: Partial<BookmarkListItem> }) => {
      for (const chunk of chunkIds(ids, BULK_IDS_MAX)) {
        await apiFetch('/api/bookmarks/batch-update', {
          method: 'POST',
          body: JSON.stringify({ ids: chunk, updates }),
        });
      }
    },
    // Optimistic: patch the affected rows in place (mirrors useBulkDelete) so
    // the favourite star / category move reflects instantly instead of after
    // a round-trip + full-list refetch.
    onMutate: ({ ids, updates }) => patchRows(queryClient, new Set(ids), updates),
    onSettled: () => invalidateBookmarkDerived(queryClient),
  });
}
